import { toUserFacingApiError } from "lib/utils/userFacingApiError";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import type { RootState } from "store";

import { ReactComponent as PencilIcon } from "assets/svgs/Editor/pencil.svg";
import { ReactComponent as FilmIcon } from "assets/svgs/navbar/videogallery.svg";
import FunctionalButton from "components/layout/FunctionalButton";
import { useToast } from "providers/ToastProvider";
import {
  ETHERNET_CONNECT_GREEN_BUTTON_CLASS,
  ETHERNET_CONNECT_GREEN_TITLE_CLASS,
  NETWORK_ROW_DISCONNECT_BUTTON_CLASS,
  NETWORK_ROW_DISCONNECT_TITLE_CLASS,
} from "lib/consts/networkSettingsActionButtons";
import {
  getRuleTargetClassIds,
} from "lib/mission/emailAlertCriteriaForm";
import {
  EmailNotificationSettings,
  EmailTemplate,
  LogEmailRule,
  fetchEmailTemplates,
  fetchLogEmailRules,
  fetchEmailNotificationSettings,
  updateEmailNotificationSettings,
  deleteEmailTemplate,
  deleteLogEmailRule,
} from "lib/mission/emailNotificationsApi";
import { getBboxServiceUrl } from "lib/utils/connection";
import { authedFetch } from "lib/utils/authedFetch";
import useIsHttpSecure from "lib/utils/useIsHttpSecure";
import useFleetArcId from "lib/utils/useFleetArcId";
import { useARC } from "providers/ARC/ARCProvider";
import EmailTemplateDialog from "./EmailTemplateDialog";
import EmailRuleDialog from "./EmailRuleDialog";
import ManageRecipientsDialog from "./ManageRecipientsDialog";

const StatusBadge: React.FC<{ on: boolean; onLabel: string; offLabel: string }> = ({
  on,
  onLabel,
  offLabel,
}) => (
  <span
    className={`inline-flex shrink-0 items-center rounded-r3 px-2 py-0.5 text-h5 font-semibold ${
      on ? "bg-primary2/20 text-primary2" : "bg-black80 text-black40"
    }`}
  >
    {on ? onLabel : offLabel}
  </span>
);

const LoadingSkeleton: React.FC = () => (
  <div className="mt-6 flex flex-col gap-6" aria-busy="true" aria-live="polite">
    {[0, 1, 2].map((i) => (
      <div key={i} className="flex flex-col gap-3">
        <div className="h-5 w-40 animate-pulse rounded-r3 bg-black80" />
        <div className="h-4 w-72 max-w-full animate-pulse rounded-r3 bg-black80/70" />
        <div className="h-24 animate-pulse rounded-r10 border border-black80 bg-black70/40" />
      </div>
    ))}
  </div>
);

const EmptyState: React.FC<{
  title: string;
  hint: string;
  actionLabel: string;
  onAction: () => void;
  disabled?: boolean;
}> = ({ title, hint, actionLabel, onAction, disabled }) => (
  <div className="rounded-r10 border border-dashed border-black80 bg-black100/30 px-4 py-6 text-center">
    <p className="text-h5 font-semibold text-white">{title}</p>
    <p className="mt-1 text-h5 text-black40">{hint}</p>
    <div className="mt-4 flex justify-center">
      <FunctionalButton
        className={ETHERNET_CONNECT_GREEN_BUTTON_CLASS}
        title={actionLabel}
        titleClassName={ETHERNET_CONNECT_GREEN_TITLE_CLASS}
        disabled={disabled}
        onClick={onAction}
      />
    </div>
  </div>
);

const isDetectionTopic = (topic: string): boolean =>
  topic.includes("rtsp_inference");

const usesOrEnabledClasses = (criteria: Record<string, unknown> | null | undefined): boolean => {
  if (!criteria || criteria.match_enabled_classes !== true) return false;
  const topic = typeof criteria.topic === "string" ? criteria.topic : "";
  return isDetectionTopic(topic);
};

async function fetchEnabledOrClassNames(
  host: string,
  isSecure: boolean,
  arcId: string | null | undefined,
): Promise<string[]> {
  const url = getBboxServiceUrl(host, "recognition", isSecure, arcId);
  if (!url) return [];
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), 8000);
  try {
    const res = await authedFetch(url, { signal: controller.signal, cache: "no-store" });
    if (!res.ok) return [];
    const data = (await res.json()) as {
      enabled?: boolean;
      classes?: Array<{ name?: string; enabled?: boolean }>;
    };
    if (data.enabled === false) return [];
    if (!Array.isArray(data.classes)) return [];
    return data.classes
      .filter((c) => c && c.enabled !== false && typeof c.name === "string" && c.name.trim())
      .map((c) => String(c.name).trim());
  } finally {
    window.clearTimeout(timer);
  }
}

const EmailNotificationsSettingsPanel: React.FC = () => {
  const { t } = useTranslation(["settings", "common"]);
  const { addToast } = useToast();
  const isSecure = useIsHttpSecure();
  const { selectedARC } = useARC();
  const fleetArcId = useFleetArcId();
  const { arcId, arcProxyHost } = useSelector((state: RootState) => state.arcProxy);
  const bboxHost = selectedARC?.host ?? arcProxyHost ?? null;
  const bboxArcId = fleetArcId ?? arcId;
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [rules, setRules] = useState<LogEmailRule[]>([]);
  const [settings, setSettings] = useState<EmailNotificationSettings | null>(null);
  const [savingDefaults, setSavingDefaults] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [recipientsDialogVisible, setRecipientsDialogVisible] = useState(false);
  const [enabledOrClasses, setEnabledOrClasses] = useState<string[]>([]);
  const [orClassesLoaded, setOrClassesLoaded] = useState(false);

  const [templateDialog, setTemplateDialog] = useState<{
    visible: boolean;
    initial: EmailTemplate | null;
  }>({ visible: false, initial: null });

  const [ruleDialog, setRuleDialog] = useState<{
    visible: boolean;
    initial: LogEmailRule | null;
  }>({ visible: false, initial: null });

  const [deletingTemplateId, setDeletingTemplateId] = useState<string | null>(null);
  const [deletingRuleId, setDeletingRuleId] = useState<string | null>(null);

  const needsOrClasses = useMemo(
    () => rules.some((rule) => usesOrEnabledClasses(rule.criteria)),
    [rules]
  );

  const loadBoth = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [tmpl, r, s] = await Promise.all([
        fetchEmailTemplates(arcProxyHost, arcId),
        fetchLogEmailRules(arcProxyHost, arcId),
        fetchEmailNotificationSettings(arcProxyHost, arcId),
      ]);
      setTemplates(tmpl);
      setRules(r);
      setSettings(s);
    } catch (e) {
      setError(toUserFacingApiError(e, t("emailNotifications.loadFailed")));
    } finally {
      setLoading(false);
    }
  }, [arcProxyHost, arcId, t]);

  useEffect(() => {
    void loadBoth();
  }, [loadBoth]);

  useEffect(() => {
    if (!needsOrClasses) {
      setEnabledOrClasses([]);
      setOrClassesLoaded(true);
      return;
    }
    if (!bboxHost) {
      setEnabledOrClasses([]);
      setOrClassesLoaded(true);
      return;
    }
    let cancelled = false;
    setOrClassesLoaded(false);
    void fetchEnabledOrClassNames(bboxHost, isSecure, bboxArcId)
      .then((names) => {
        if (!cancelled) {
          setEnabledOrClasses(names);
          setOrClassesLoaded(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setEnabledOrClasses([]);
          setOrClassesLoaded(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [needsOrClasses, bboxHost, isSecure, bboxArcId]);

  const refreshTemplates = useCallback(async () => {
    try {
      setTemplates(await fetchEmailTemplates(arcProxyHost, arcId));
    } catch (e) {
      addToast({
        type: "error",
        message: toUserFacingApiError(e, t("emailNotifications.refreshTemplatesFailed")),
      });
    }
  }, [addToast, arcProxyHost, arcId, t]);

  const refreshRules = useCallback(async () => {
    try {
      setRules(await fetchLogEmailRules(arcProxyHost, arcId));
    } catch (e) {
      addToast({
        type: "error",
        message: toUserFacingApiError(e, t("emailNotifications.refreshRulesFailed")),
      });
    }
  }, [addToast, arcProxyHost, arcId, t]);

  const handleSaveRecipients = useCallback(
    async (emails: string[]) => {
      if (emails.length === 0) {
        addToast({ type: "error", message: t("emailNotifications.atLeastOneRecipient") });
        return;
      }
      setSavingDefaults(true);
      try {
        const updated = await updateEmailNotificationSettings(
          { to: emails, cc: [], bcc: [] },
          arcProxyHost,
          arcId
        );
        setSettings(updated);
        setRecipientsDialogVisible(false);
      } catch (e) {
        addToast({
          type: "error",
          message: toUserFacingApiError(e, t("emailNotifications.saveDefaultsFailed")),
        });
      } finally {
        setSavingDefaults(false);
      }
    },
    [addToast, arcProxyHost, arcId, t]
  );

  const handleDeleteTemplate = useCallback(
    async (template: EmailTemplate) => {
      if (!window.confirm(t("emailNotifications.deleteConfirmTemplate", { name: template.name }))) {
        return;
      }
      setDeletingTemplateId(template.id);
      try {
        await deleteEmailTemplate(template.id, arcProxyHost, arcId);
        setTemplates((prev) => prev.filter((item) => item.id !== template.id));
      } catch (e) {
        addToast({
          type: "error",
          message: toUserFacingApiError(e, t("emailNotifications.deleteFailed")),
        });
      } finally {
        setDeletingTemplateId(null);
      }
    },
    [addToast, arcProxyHost, arcId, t]
  );

  const handleDeleteRule = useCallback(
    async (rule: LogEmailRule) => {
      if (!window.confirm(t("emailNotifications.deleteConfirmRule", { name: rule.name }))) {
        return;
      }
      setDeletingRuleId(rule.id);
      try {
        await deleteLogEmailRule(rule.id, arcProxyHost, arcId);
        setRules((prev) => prev.filter((item) => item.id !== rule.id));
      } catch (e) {
        addToast({
          type: "error",
          message: toUserFacingApiError(e, t("emailNotifications.deleteFailed")),
        });
      } finally {
        setDeletingRuleId(null);
      }
    },
    [addToast, arcProxyHost, arcId, t]
  );

  const openNewRule = useCallback(() => {
    if (templates.length === 0) {
      addToast({ type: "error", message: t("emailNotifications.newRuleNeedsTemplate") });
      return;
    }
    setRuleDialog({ visible: true, initial: null });
  }, [templates.length, addToast, t]);

  const templateMap = Object.fromEntries(templates.map((tmpl) => [tmpl.id, tmpl.name]));
  const hasNoRecipients =
    !!settings &&
    !settings.defaultRecipients.to.length &&
    !settings.defaultRecipients.cc.length &&
    !settings.defaultRecipients.bcc.length;

  return (
    <div className="flex min-w-0 flex-col">
      <div className="shrink-0 border-b border-black80 pb-4">
        <p className="text-h4 font-semibold text-white">{t("emailNotifications.title")}</p>
        <p className="mt-1 text-h5 text-black40">{t("emailNotifications.description")}</p>
      </div>

      {error ? (
        <p className="mt-4 text-h5 text-red-400" role="alert">
          {error}
        </p>
      ) : loading ? (
        <LoadingSkeleton />
      ) : (
        <>
          {hasNoRecipients ? (
            <div className="mt-4 rounded-r5 border border-yellow-700/60 bg-yellow-950/50 px-4 py-3">
              <p className="text-h5 font-semibold text-yellow-300">
                {t("emailNotifications.noRecipientsTitle")}
              </p>
              <p className="mt-0.5 text-h5 text-yellow-400/80">
                {t("emailNotifications.noRecipientsHint")}
              </p>
            </div>
          ) : null}

          {/* Who */}
          <div className="mt-6">
            <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-h4 font-semibold text-white">
                  {t("emailNotifications.whoSection")}
                </p>
                <p className="mt-1 text-h5 text-black40">
                  {t("emailNotifications.whoSectionDescription")}
                </p>
              </div>
              <FunctionalButton
                className={ETHERNET_CONNECT_GREEN_BUTTON_CLASS}
                title={t("emailNotifications.manageRecipients")}
                titleClassName={ETHERNET_CONNECT_GREEN_TITLE_CLASS}
                onClick={() => setRecipientsDialogVisible(true)}
              />
            </div>
            {(settings?.defaultRecipients.to.length ?? 0) === 0 ? (
              <div className="rounded-r10 border border-dashed border-black80 bg-black100/30 px-4 py-5 text-center">
                <p className="text-h5 text-black40">
                  {t("emailNotifications.noRecipientsInList")}
                </p>
              </div>
            ) : (
              <ul className="overflow-hidden rounded-r10 border border-black80">
                {settings?.defaultRecipients.to.map((email) => (
                  <li
                    key={email}
                    className="border-b border-black80 px-3 py-2.5 text-h5 text-white last:border-b-0"
                  >
                    {email}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* When */}
          <div className="mt-8">
            <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-h4 font-semibold text-white">
                  {t("emailNotifications.whenSection")}
                </p>
                <p className="mt-1 text-h5 text-black40">
                  {t("emailNotifications.whenSectionDescription")}
                </p>
              </div>
              {rules.length > 0 ? (
                <FunctionalButton
                  className={ETHERNET_CONNECT_GREEN_BUTTON_CLASS}
                  title={t("emailNotifications.newRule")}
                  titleClassName={ETHERNET_CONNECT_GREEN_TITLE_CLASS}
                  disabled={templates.length === 0}
                  onClick={openNewRule}
                />
              ) : null}
            </div>
            {rules.length === 0 ? (
              <EmptyState
                title={t("emailNotifications.noRulesTitle")}
                hint={
                  templates.length === 0
                    ? t("emailNotifications.newRuleNeedsTemplate")
                    : t("emailNotifications.noRulesHint")
                }
                actionLabel={t("emailNotifications.newRule")}
                onAction={openNewRule}
                disabled={templates.length === 0}
              />
            ) : (
              <ul className="overflow-hidden rounded-r10 border border-black80">
                {rules.map((rule) => {
                  const deleting = deletingRuleId === rule.id;
                  const templateName = templateMap[rule.templateId] ?? rule.templateId;
                  const followsOr = usesOrEnabledClasses(rule.criteria);
                  const specificClasses = getRuleTargetClassIds(rule.criteria);
                  let activeClassNames: string[] | null = null;
                  let activeClassesStatus: string | null = null;
                  if (followsOr) {
                    if (!orClassesLoaded) {
                      activeClassesStatus = t("emailNotifications.activeClassesLoading");
                    } else if (enabledOrClasses.length === 0) {
                      activeClassesStatus = t("emailNotifications.activeClassesNone");
                    } else {
                      activeClassNames = enabledOrClasses;
                    }
                  } else if (specificClasses && specificClasses.length > 0) {
                    activeClassNames = specificClasses;
                  }
                  return (
                    <li
                      key={rule.id}
                      className="flex items-start justify-between gap-3 border-b border-black80 p-3 last:border-b-0"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="min-w-0 truncate text-h5 font-semibold text-white">
                            {rule.name}
                          </p>
                          <StatusBadge
                            on={rule.enabled}
                            onLabel={t("emailNotifications.statusOn")}
                            offLabel={t("emailNotifications.statusOff")}
                          />
                          {rule.extractClip ? (
                            <span title={t("emailNotifications.alertClipEnabled")}>
                              <FilmIcon className="!h-4 !w-4 h-4 w-4 shrink-0 text-primary2" />
                            </span>
                          ) : null}
                        </div>
                        <p className="mt-0.5 truncate text-h5 text-black40">{templateName}</p>
                        {activeClassNames ? (
                          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                            <span className="text-h5 text-black40">
                              {t("emailNotifications.activeClassesLabel")}
                            </span>
                            {activeClassNames.map((name) => (
                              <span
                                key={name}
                                className="inline-flex items-center rounded-r3 bg-primary2/20 px-2 py-0.5 text-h5 font-semibold text-primary2"
                              >
                                {name}
                              </span>
                            ))}
                          </div>
                        ) : activeClassesStatus ? (
                          <p className="mt-1 text-h5 text-black40">{activeClassesStatus}</p>
                        ) : null}
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <button
                          type="button"
                          className="shrink-0 rounded-r3 p-1 text-black40 hover:bg-black80 hover:text-white disabled:pointer-events-none disabled:opacity-40"
                          disabled={deleting}
                          aria-label={t("emailNotifications.editRule")}
                          onClick={() => setRuleDialog({ visible: true, initial: rule })}
                        >
                          <PencilIcon className="!h-4 !w-4 h-4 w-4 shrink-0 text-inherit" />
                        </button>
                        <FunctionalButton
                          className={NETWORK_ROW_DISCONNECT_BUTTON_CLASS}
                          title={deleting ? t("common:ellipsis") : t("common:delete")}
                          titleClassName={NETWORK_ROW_DISCONNECT_TITLE_CLASS}
                          disabled={deleting}
                          onClick={() => void handleDeleteRule(rule)}
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Content / Templates */}
          <div className="mt-8">
            <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-h4 font-semibold text-white">
                  {t("emailNotifications.contentSection")}
                </p>
                <p className="mt-1 text-h5 text-black40">
                  {t("emailNotifications.contentSectionDescription")}
                </p>
              </div>
              {templates.length > 0 ? (
                <FunctionalButton
                  className={ETHERNET_CONNECT_GREEN_BUTTON_CLASS}
                  title={t("emailNotifications.newTemplate")}
                  titleClassName={ETHERNET_CONNECT_GREEN_TITLE_CLASS}
                  onClick={() => setTemplateDialog({ visible: true, initial: null })}
                />
              ) : null}
            </div>
            {templates.length === 0 ? (
              <EmptyState
                title={t("emailNotifications.noTemplatesTitle")}
                hint={t("emailNotifications.noTemplatesHint")}
                actionLabel={t("emailNotifications.newTemplate")}
                onAction={() => setTemplateDialog({ visible: true, initial: null })}
              />
            ) : (
              <ul className="overflow-hidden rounded-r10 border border-black80">
                {templates.map((template) => {
                  const deleting = deletingTemplateId === template.id;
                  const preview =
                    template.subjectTemplate.length > 48
                      ? `${template.subjectTemplate.slice(0, 48)}…`
                      : template.subjectTemplate;
                  return (
                    <li
                      key={template.id}
                      className="flex items-start justify-between gap-3 border-b border-black80 p-3 last:border-b-0"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="min-w-0 truncate text-h5 font-semibold text-white">
                            {template.name}
                          </p>
                          <StatusBadge
                            on={template.enabled}
                            onLabel={t("emailNotifications.statusOn")}
                            offLabel={t("emailNotifications.statusOff")}
                          />
                        </div>
                        <p className="mt-0.5 truncate text-h5 text-black40">
                          &ldquo;{preview}&rdquo;
                        </p>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <button
                          type="button"
                          className="shrink-0 rounded-r3 p-1 text-black40 hover:bg-black80 hover:text-white disabled:pointer-events-none disabled:opacity-40"
                          disabled={deleting}
                          aria-label={t("emailNotifications.editTemplate")}
                          onClick={() =>
                            setTemplateDialog({ visible: true, initial: template })
                          }
                        >
                          <PencilIcon className="!h-4 !w-4 h-4 w-4 shrink-0 text-inherit" />
                        </button>
                        <FunctionalButton
                          className={NETWORK_ROW_DISCONNECT_BUTTON_CLASS}
                          title={deleting ? t("common:ellipsis") : t("common:delete")}
                          titleClassName={NETWORK_ROW_DISCONNECT_TITLE_CLASS}
                          disabled={deleting}
                          onClick={() => void handleDeleteTemplate(template)}
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </>
      )}

      <ManageRecipientsDialog
        visible={recipientsDialogVisible}
        initialEmails={settings?.defaultRecipients.to ?? []}
        saving={savingDefaults}
        onClose={() => setRecipientsDialogVisible(false)}
        onSave={handleSaveRecipients}
      />

      <EmailTemplateDialog
        visible={templateDialog.visible}
        initial={templateDialog.initial}
        onClose={() => setTemplateDialog((prev) => ({ ...prev, visible: false }))}
        onSaved={() => void refreshTemplates()}
      />

      <EmailRuleDialog
        visible={ruleDialog.visible}
        initial={ruleDialog.initial}
        templates={templates}
        onClose={() => setRuleDialog((prev) => ({ ...prev, visible: false }))}
        onSaved={() => void refreshRules()}
      />
    </div>
  );
};

export default EmailNotificationsSettingsPanel;
